export interface ApiResult<T> {
  data?: T;
  error?: string;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { headers: inputHeaders, ...requestOptions } = options;
  const headers = buildHeaders(inputHeaders, requestOptions.body);
  const response = await fetch(path, {
    credentials: "include",
    ...requestOptions,
    ...(headers ? { headers } : {})
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};
  if (!response.ok) {
    if (response.status === 401 && shouldExpireAdminSession(path)) {
      window.dispatchEvent(new CustomEvent("admin-session-expired"));
    }
    throw new Error(payload.message || payload.error || "请求失败");
  }
  return payload as T;
}

function shouldExpireAdminSession(path: string) {
  return !(
    path === "/api/auth/login" ||
    path === "/api/auth/totp-login" ||
    path === "/api/admin/settings/password" ||
    path.startsWith("/api/admin/security/totp/")
  );
}

function buildHeaders(inputHeaders: HeadersInit | undefined, body: BodyInit | null | undefined) {
  if (body === undefined || body === null || body instanceof FormData) return inputHeaders;

  const headers = new Headers(inputHeaders);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return headers;
}

export const client = {
  login(username: string, password: string) {
    return api<LoginResult>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password })
    });
  },
  totpLogin(challengeId: string, code: string) {
    return api<AuthenticatedLoginResult>("/api/auth/totp-login", {
      method: "POST",
      body: JSON.stringify({ challengeId, code })
    });
  },
  logout() {
    return api<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
  },
  me() {
    return api<{ authenticated: boolean; admin?: AdminProfile }>("/api/auth/me");
  },
  dashboard() {
    return api<Dashboard>("/api/admin/dashboard");
  },
  settings() {
    return api<Settings>("/api/admin/settings");
  },
  updateOcr(input: Partial<OcrSettings> & { apiKey?: string }) {
    return api<OcrSettings>("/api/admin/settings/ocr", {
      method: "PUT",
      body: JSON.stringify(input)
    });
  },
  updateBailian(input: Partial<BailianSettings> & { apiKey?: string }) {
    return api<BailianSettings>("/api/admin/settings/bailian", {
      method: "PUT",
      body: JSON.stringify(input)
    });
  },
  updateInvoiceOcr(input: Partial<InvoiceOcrSettings> & { accessKeyId?: string; accessKeySecret?: string }) {
    return api<InvoiceOcrSettings>("/api/admin/settings/invoice-ocr", {
      method: "PUT",
      body: JSON.stringify(input)
    });
  },
  updateRetention(input: Partial<RetentionSettings>) {
    return api<RetentionSettings>("/api/admin/settings/retention", {
      method: "PUT",
      body: JSON.stringify(input)
    });
  },
  changePassword(input: { currentPassword: string; newPassword: string; confirmPassword: string }) {
    return api<SecuritySettings>("/api/admin/settings/password", {
      method: "PUT",
      body: JSON.stringify(input)
    });
  },
  startTotpSetup() {
    return api<TotpSetupStart>("/api/admin/security/totp/setup/start", { method: "POST" });
  },
  confirmTotpSetup(code: string) {
    return api<TotpSetupConfirm>("/api/admin/security/totp/setup/confirm", {
      method: "POST",
      body: JSON.stringify({ code })
    });
  },
  disableTotp(input: { currentPassword: string; code: string }) {
    return api<SecuritySettings>("/api/admin/security/totp/disable", {
      method: "POST",
      body: JSON.stringify(input)
    });
  },
  regenerateRecoveryCodes(input: { currentPassword: string; code: string }) {
    return api<TotpSetupConfirm>("/api/admin/security/totp/recovery-codes", {
      method: "POST",
      body: JSON.stringify(input)
    });
  },
  companies() {
    return api<Company[]>("/api/admin/companies");
  },
  saveCompany(company: CompanyInput, id?: number) {
    return api<Company>(id ? `/api/admin/companies/${id}` : "/api/admin/companies", {
      method: id ? "PUT" : "POST",
      body: JSON.stringify(company)
    });
  },
  invoices(query: Record<string, string> = {}) {
    const search = new URLSearchParams(query);
    return api<{ statuses: string[]; items: Invoice[] }>(`/api/admin/invoices?${search.toString()}`);
  },
  uploadInvoice(file: File) {
    const form = new FormData();
    form.append("file", file);
    return api<Invoice>("/api/admin/invoices/upload", {
      method: "POST",
      body: form
    });
  },
  updateInvoice(id: number, patch: Partial<Invoice>) {
    return api<Invoice>(`/api/admin/invoices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch)
    });
  },
  invoiceDownloadUrl(id: number) {
    return api<{ url: string }>(`/api/admin/invoices/${id}/download`);
  },
  invoiceFileUrl(id: number, filename?: string) {
    return `/api/admin/invoices/${id}/file${filename ? `/${encodeURIComponent(filename)}` : ""}`;
  },
  invoicePreviewUrl(id: number, filename?: string) {
    return `/api/admin/invoices/${id}/file${filename ? `/${encodeURIComponent(filename)}` : ""}?preview=1`;
  },
  exports() {
    return api<{ items: ExportRecord[] }>("/api/admin/exports");
  },
  generateExport(periodMonth: string, includeA4Layout = true) {
    return api<ExportRecord>("/api/admin/exports", {
      method: "POST",
      body: JSON.stringify({ periodMonth, includeA4Layout })
    });
  },
  exportDownloadUrl(id: number, kind: "zip" | "excel") {
    return api<{ url: string }>(`/api/admin/exports/${id}/download?kind=${kind}`);
  },
  exportFileUrl(id: number, kind: "zip" | "excel", filename?: string) {
    return `/api/admin/exports/${id}/file${filename ? `/${encodeURIComponent(filename)}` : ""}?kind=${kind}`;
  },
  logs() {
    return api<{ items: AuditLog[] }>("/api/admin/audit-logs");
  },
  runRetention() {
    return api<{ deletedFiles: number; deletedExports: number }>("/api/admin/retention/run", { method: "POST" });
  },
  retryInvoiceOcr(id: number) {
    return api<Invoice>(`/api/admin/invoices/${id}/ocr/retry`, { method: "POST" });
  },
  retryInvoiceClassification(id: number) {
    return api<Invoice>(`/api/admin/invoices/${id}/classify/retry`, { method: "POST" });
  },
  reprocessInvoice(id: number) {
    return api<InvoiceReprocessResult>(`/api/admin/invoices/${id}/reprocess`, { method: "POST" });
  },
  retryInvoiceImage(id: number) {
    return api<Invoice>(`/api/admin/invoices/${id}/image/retry`, { method: "POST" });
  },
  retryMonthOcr(periodMonth: string) {
    return api<{ queued: number }>("/api/admin/invoices/ocr/retry-month", {
      method: "POST",
      body: JSON.stringify({ periodMonth })
    });
  }
};

export type AuthenticatedLoginResult = {
  ok: true;
  requiresTotp: false;
  admin: AdminProfile;
  expiresAt: string;
};

export type TotpChallengeResult = {
  ok: true;
  requiresTotp: true;
  challengeId: string;
  expiresInSeconds: number;
  admin: Pick<AdminProfile, "adminId" | "username" | "roleCode" | "roleName" | "status" | "isSuperAdmin" | "totpEnabled">;
};

export type LoginResult = AuthenticatedLoginResult | TotpChallengeResult;

export interface TotpSetupStart {
  secret: string;
  otpauthUri: string;
  createdAt: string;
}

export interface TotpSetupConfirm {
  security: SecuritySettings;
  recoveryCodes: string[];
}

export interface InvoiceReprocessResult {
  invoice: Invoice;
  actions: string[];
  message: string;
}

export interface Dashboard {
  month: string;
  counts: Array<{ status: string; count: number }>;
  monthly: { count: number; total: number };
  monthlyByDirection?: Array<{ invoice_direction: string; count: number; total: number }>;
  monthlyByDocumentType?: Array<{ document_type: string; count: number; total: number }>;
  companies: Array<{
    name: string;
    count: number;
    total: number;
    input_count?: number;
    input_total?: number;
    output_count?: number;
    output_total?: number;
  }>;
  storageMode: string;
  retentionDays: number;
}

export interface Settings {
  security: SecuritySettings;
  invoiceOcr: InvoiceOcrSettings;
  bailian: BailianSettings;
  cos: {
    bucket: string;
    region: string;
    prefixBase: string;
    credentialsConfigured: boolean;
    privateReadWrite: boolean;
  };
  retention: RetentionSettings;
  wecom: {
    callbackUrl: string;
    configured: boolean;
    corpIdConfigured: boolean;
    agentIdConfigured: boolean;
    secretConfigured: boolean;
    tokenConfigured: boolean;
    aesKeyConfigured: boolean;
  };
}

export interface SecuritySettings {
  passwordConfigured: boolean;
  passwordUpdatedAt: string | null;
  username: string;
  roleName: string;
  totpEnabled: boolean;
  totpConfirmedAt: string | null;
  totpPending: boolean;
  recoveryCodesRemaining: number;
}

export interface AdminProfile {
  adminId: number;
  username: string;
  roleCode: string;
  roleName: string;
  status: string;
  isSuperAdmin: boolean;
  permissions: string[];
  lastLoginAt: string;
  passwordUpdatedAt: string;
  totpEnabled: boolean;
  totpConfirmedAt: string;
  totpSetupRequired: boolean;
  recoveryCodesRemaining: number;
}

export interface OcrSettings {
  provider: string;
  baseUrl: string;
  model: string;
  apiKeyConfigured: boolean;
}

export type BailianSettings = OcrSettings;

export interface InvoiceOcrSettings {
  provider: string;
  endpoint: string;
  region: string;
  pageNo: number;
  accessKeyConfigured: boolean;
}

export interface RetentionSettings {
  invoiceFileRetentionDays: number;
  exportRetentionDays: number;
  cleanupFrequency: "daily";
}

export interface Company {
  id: number;
  name: string;
  short_name: string;
  tax_id: string;
  aliases: string;
  enabled: number;
}

export interface CompanyInput {
  name: string;
  short_name: string;
  tax_id: string;
  aliases: string[];
  enabled: boolean;
}

export interface Invoice {
  id: number;
  file_id: number;
  company_id: number | null;
  source?: string;
  submission_id?: number | null;
  submitted_at?: string;
  external_message_id?: string;
  submission_message_type?: string;
  submitter_external_user_id?: string;
  submitter_name?: string;
  company_name?: string;
  company_short_name?: string;
  original_filename?: string;
  mime_type?: string;
  invoice_type?: string;
  invoice_code?: string;
  invoice_number?: string;
  invoice_date?: string;
  document_type?: "invoice" | "voucher" | string;
  voucher_type?: "social_security" | "medical_insurance" | "payment_receipt" | "other_voucher" | "unknown" | string | null;
  voucher_period_month?: string | null;
  invoice_direction?: "input" | "output" | "unknown" | string;
  seller_name?: string;
  seller_tax_id?: string;
  buyer_name?: string;
  buyer_tax_id?: string;
  total_amount?: number;
  status: string;
  duplicate_of_id?: number;
  manual_note?: string;
  storage_status?: string;
  image_process_status?: string;
  image_process_error?: string;
  image_processed_at?: string;
  retention_delete_after?: string;
  retention_deleted_at?: string;
  created_at: string;
}

export interface ExportRecord {
  id: number;
  period_month: string;
  status: string;
  record_count: number;
  storage_status: string;
  include_a4_layout?: number;
  a4_layout_count?: number;
  a4_unprocessed_count?: number;
  error_message?: string | null;
  retention_delete_after: string;
  retention_deleted_at?: string;
  created_at: string;
}

export interface AuditLog {
  id: number;
  actor: string;
  action: string;
  target_type: string;
  target_id: string;
  created_at: string;
}
